import React, { useEffect, useMemo, useState, useRef} from "react";
import { scrollToTop } from "./landing/wizard/wizardScroll";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { getAllGames } from "../../api/user";
import { SectionError } from "../../components/Skeleton/LoadError";
import PublicLeagueRow from "./landing/PublicLeagueRow";
import CompletedLeagueTile from "./landing/CompletedLeagueTile";
import LeagueEmptyState, { EmptyStateArt } from "./landing/LeagueEmptyState";
import { getLeagueStatus } from "./landing/leagueStatus";
import { MdOutlineLayers, MdOutlinePersonAddAlt } from "react-icons/md";
import usePullToRefresh from "../../hooks/usePullToRefresh";
import { useRestoreListPosition } from "../../utils/listPosition";
import PullToRefreshIndicator from "../../components/Other/PullToRefreshIndicator";

// My Leagues — every league this user is in, in one list.
//
// NOT its own card system. The listing, the tabs, the row, the empty states and
// the skeleton are the ones Public Leagues and Cash Games use; `phaseOf` is even
// the same rule, so a league can't be filed as upcoming in one list and running
// in another. Only three things are specific to this screen, and all three are
// content rather than layout:
//
//   • the header ("All your leagues in one place")
//   • the footer ("Want another league?")
//   • the row itself is the sitewide preview card — a league reads the same
//     here as it does anywhere else (see previewMetaParts)
//
// Tab order matches Public Leagues so the two read identically. The default tab
// does not: shopping starts at what's coming, but checking on your own leagues
// starts at what's running.
const TABS = [
  { key: "soon", label: "Starting soon" },
  { key: "live", label: "Live now" },
  { key: "done", label: "Completed" },
];

const EMPTY = {
  live: {
    title: "Nothing live right now",
    body: "Leagues you're in appear here once they start.",
  },
  soon: {
    title: "Nothing starting soon",
    body: "Join a league before it begins and it lands here.",
  },
  done: {
    title: "No finished leagues yet",
    body: "Your results land here once a league wraps.",
  },
};

// One rule for which tab a league belongs in, shared with the Public Leagues
// list, so a league can't be filed as upcoming in one place and running in the
// other.
const phaseOf = (game) => {
  const tone = getLeagueStatus(game)?.tone;
  if (game?.status === "completed" || tone === "ended") return "done";
  if (tone === "live") return "live";
  return "soon";
};

const startMs = (game) => {
  const t = game?.start_date ? new Date(game.start_date).getTime() : NaN;
  return Number.isNaN(t) ? Infinity : t;
};

const MyLeagues = () => {
  // Contest.jsx owns the search field in its header band.
  const { search } = useOutletContext() || {};
  // Opens on what is running — see the note on TABS.
  //
  // ...unless the caller names a tab. Client Rev 3, both payoff screens: "My
  // Leagues link should point to the My Leagues screen's Starting Soon tab." A
  // league that was just created has not started, so landing on "Live now" would
  // show the organiser an empty list and hide the thing they just made.
  // `?tab=soon` is the whole contract; an unknown value falls back to the
  // default rather than blanking the screen.
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab");
  const [tab, setTabState] = useState(
    TABS.some((t) => t.key === requested) ? requested : "live"
  );

  // Clicking a tab WRITES the tab into the address.
  //
  // Client, on the standalone leaderboard's back arrow: "My Leagues > view all >
  // completed > view results > leaderboard preview > back arrow > returns you to
  // the 'Live now' screen. You should go back to the 'Completed' tab you came
  // from."
  //
  // It did, and this is why: clicking a tab used to DELETE `?tab=`, on the
  // reasoning that a reload should not snap the user back to the tab a link had
  // asked for. So the history entry the back arrow returns to was a bare
  // /contest/my-games, and the state below opens that on "live" — the default.
  //
  // Keeping the param in step with the active tab fixes the back arrow and costs
  // nothing on reload, because the param now always describes the tab the user
  // is actually looking at rather than one a stale link once requested.
  // `replace`, so moving between tabs does not fill the history with entries the
  // back button then has to walk through one at a time.
  const setTab = (key) => {
    setTabState(key);
    params.set("tab", key);
    setParams(params, { replace: true });
  };
  const [state, setState] = useState({ loading: true, error: false, data: [] });

  const refreshFunction = async () => {
    try {
      const res = await getAllGames();
      setState({ loading: false, error: false, data: res?.data?.data || [] });
    } catch (e) {
      console.error("My leagues refresh failed:", e);
      setState({ loading: false, error: true, data: state.data });
    }
  };


  // Client: "When you open the mobile version Cash Games > View All from League
  // Play Home, the page does not always start at the very top where the 'Cash
  // Games' title is visible... Please make sure: All three View All pages open
  // at the top of the page."
  //
  // They did not, and it is the same cause the create-league wizard had: the app
  // does not scroll the WINDOW. `.mainWrapper` is the scroller, so React Router
  // swapping the route leaves that element at whatever offset the PREVIOUS
  // screen was scrolled to — and League Play Home is usually scrolled well down
  // by the time anyone reaches a View All link. scrollToTop walks up to the real
  // scroller instead of calling window.scrollTo, which is a no-op here.
  const topRef = useRef(null);
  useEffect(() => {
    scrollToTop(topRef.current);
  }, []);

  const { containerProps, isRefreshing, pullDistance } = usePullToRefresh({
    onRefresh: refreshFunction,
    disabled: false,
  });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: false }));
    getAllGames()
      .then((res) => {
        if (cancelled) return;
        setState({ loading: false, error: false, data: res?.data?.data || [] });
      })
      .catch((e) => {
        console.error("My leagues load failed:", e);
        if (!cancelled) setState({ loading: false, error: true, data: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const searched = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return state.data;
    return state.data.filter(
      (g) =>
        (g.title && g.title.toLowerCase().includes(q)) ||
        (g.description && g.description.toLowerCase().includes(q))
    );
  }, [state.data, search]);

  const buckets = useMemo(() => {
    const out = { live: [], soon: [], done: [] };
    searched.forEach((g) => out[phaseOf(g)].push(g));
    // What's running and what's coming read soonest-first; what's over reads
    // most-recent-first, because the last result is the one being looked for.
    out.live.sort((a, b) => startMs(a) - startMs(b));
    out.soon.sort((a, b) => startMs(a) - startMs(b));
    out.done.sort((a, b) => startMs(b) - startMs(a));
    return out;
  }, [searched]);

  const rows = buckets[tab];

  // "...and, let's assume one day this is a long list, it takes you back to the
  // same place in the list." Runs once the rows are on the page — before that
  // there is nothing to scroll to. See utils/listPosition.js.
  useRestoreListPosition(!state.loading && rows.length > 0);

  return (
    <>
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <div className="px-4 md:px-8 pb-24 md:pb-16" {...containerProps}>
        <div ref={topRef} className="max-w-screen-md mx-auto">
        {/* Screen page title, house rule: Montserrat bold in teal. */}
        <h1 className="font-montserrat font-bold text-title-subpage md:text-title-page text-pageTitle">
          My Leagues
        </h1>
        {/* Header card, per the design: what this screen collects. */}
        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 md:p-5">
          <MdOutlineLayers size={26} className="mt-0.5 shrink-0 text-[#0F4146]" />
          <div className="min-w-0">
            <h2 className="font-montserrat font-bold text-base text-[#1A1A1A]">
              All your leagues in one place.
            </h2>
            <p className="mt-0.5 text-sm text-secondary">
              Track public leagues, private leagues, and cash games here.
            </p>
          </div>
        </div>

        {/* Client Rev 4: these are global League Play fixes, so the tab group is
            the same one Public Leagues uses — thirds across the full width on
            mobile, a compact left-aligned row with a 48px gap on desktop. */}
        <div
          role="tablist"
          className="mt-5 grid grid-cols-3 border-b border-divider md:flex md:items-center md:gap-12"
        >
          {TABS.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.key)}
                className={`-mb-px w-full border-b-2 pb-3 text-center text-sm transition-colors md:w-auto md:shrink-0 md:text-left md:text-base ${
                  active
                    ? "border-greenXD font-semibold text-greenXD"
                    : "border-transparent text-secondary hover:text-[#0F172A]"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* One results container on desktop, whatever the tab holds — rows, the
            empty state, an error or the skeleton. Mobile keeps the open list.

            ...except on Completed. Client, League Play Dev Request: "Currently,
            the Completed tab under My Leagues is using the public leagues
            completed structure. For 'My Leagues', we will use the card
            structure, not the divider-line structure." And, from note 14856:
            "The same cards will show under the 'My Leagues' section of the
            'League Home' screen and the 'My Leagues' 'Completed' tab."

            The card carries its own border and white surface, so the desktop
            panel is dropped for that tab only — otherwise every result would be
            a bordered card sitting inside a bordered card. Starting soon and
            Live now are untouched: those are still the sitewide preview row, and
            the client's note is about Completed alone.

            Public Leagues' own Completed tab is also untouched, on the client's
            instruction: "Public Leagues Completed tab: Divider-based directory
            rows -- no change; this is good." */}
        <div
          className={
            tab === "done"
              ? "mt-4"
              : "mt-4 md:rounded-2xl md:border md:border-gray-200 md:bg-white md:px-5"
          }
        >
          {state.loading ? (
            <div className="divide-y divide-divider">
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-2 py-4">
                  <div className="h-4 w-28 animate-pulse rounded bg-[#F3F4F6]" />
                  <div className="h-5 w-3/4 animate-pulse rounded bg-[#F3F4F6]" />
                  <div className="h-4 w-1/2 animate-pulse rounded bg-[#F3F4F6]" />
                </div>
              ))}
            </div>
          ) : state.error ? (
            <div className="py-4">
              <SectionError
                message="Your leagues could not be loaded"
                onRetry={() => window.location.reload()}
              />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-2 md:py-4">
              {/* A search that matches nothing is a different situation from a
                  phase that's genuinely empty, and shouldn't claim the phase is. */}
              {search ? (
                <p className="py-10 text-center text-sm text-secondary">
                  None of your leagues match “{search}”.
                </p>
              ) : (
                <LeagueEmptyState
                  art={<EmptyStateArt variant="myLeagues" />}
                  title={EMPTY[tab].title}
                  body={EMPTY[tab].body}
                />
              )}
            </div>
          ) : (
            tab === "done" ? (
            // The identical component My Leagues on League Home renders, so a
            // finished league reads the same in both places by construction.
            <div className="space-y-3">
              {rows.map((game) => (
                <CompletedLeagueTile key={game.id} game={game} />
              ))}
            </div>
            ) : (
            <div className="divide-y divide-divider">
              {rows.map((game) => (
                <PublicLeagueRow key={game.id} game={game} member />
              ))}
            </div>
            )
          )}
        </div>

        {/* Below the list on purpose: it's the answer to having read it and not
            found what you wanted. */}
        <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex items-start gap-3">
            <MdOutlinePersonAddAlt size={24} className="mt-0.5 shrink-0 text-[#0F4146]" />
            <div className="min-w-0">
              <h2 className="font-montserrat font-bold text-base md:text-lg text-[#0F4146]">
                Want another league?
              </h2>
              <p className="mt-0.5 text-sm text-secondary">
                Browse public leagues and cash games, or join with a private invite.
              </p>
            </div>
          </div>
          {/* League Play is the honest destination for all three of those: it
              holds the public list, the cash games and the invite-code entry. */}
          <Link
            to="/contest"
            className="mt-4 flex w-full items-center justify-center rounded-xl bg-greenXD py-3.5 font-bold text-base text-white transition-colors hover:bg-[#4F9A41]"
          >
            Browse Leagues
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default MyLeagues;
